import React, { type ComponentType, useCallback, useMemo } from 'react';

import Loadable from 'react-loadable';

import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { CARDS_EXTENSION_TYPE } from '@atlassian/cards-extension/constants';
import { type CardsExtensionParameters } from '@atlassian/cards-extension/types';
import { carouselExtensionType, type CarouselParameters } from '@atlassian/carousel-extension';
import {
	ExtensionMode,
	type ProductDependenciesProviderProps,
	SECTION_EXTENSION_TYPE,
	SPOTLIGHT_EXTENSION_TYPE,
} from '@atlassian/company-hub-common';
import {
	LINKS_MENU_EXTENSION_TYPE,
	type LinksMenuParameters,
} from '@atlassian/links-menu-extension';
import { type SectionParameters } from '@atlassian/section-extension';
import type { SpotlightParameters } from '@atlassian/spotlight-extension';

export interface HubExtensionHandlersProps {
	contentId: string;
	PropsProvider: ComponentType<ProductDependenciesProviderProps>;
}

const SpotlightExtensionLoader = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_spotlight-extension" */ '@atlassian/spotlight-extension'
			)
		).SpotlightExtension,
	loading: () => (
		<UFOLoadHold name="spotlight-extension-loader">
			<></>
		</UFOLoadHold>
	),
});

const LinksMenuLoader = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_links-menu-extension" */ '@atlassian/links-menu-extension'
			)
		).LinksMenu,
	loading: () => (
		<UFOLoadHold name="links-menu-extension-loader">
			<></>
		</UFOLoadHold>
	),
});

export const CardsExtensionLoader = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_cards-extension" */ '@atlassian/cards-extension'
			)
		).CardsExtension,
	loading: () => (
		<UFOLoadHold name="cards-extension-loader">
			<></>
		</UFOLoadHold>
	),
});

const CarouselLoader = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_carousel-extension" */ '@atlassian/carousel-extension'
			)
		).Carousel,
	loading: () => (
		<UFOLoadHold name="carousel-extension-loader">
			<></>
		</UFOLoadHold>
	),
});

const SectionExtensionLoader = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_section-extension" */ '@atlassian/section-extension'
			)
		).SectionExtension,
	loading: () => (
		<UFOLoadHold name="section-extension-loader">
			<></>
		</UFOLoadHold>
	),
});

export const useHubExtensionHandlers = (props: HubExtensionHandlersProps) => {
	const { contentId, PropsProvider } = props;
	const extensionHandlers: ExtensionHandlers = {
		[SPOTLIGHT_EXTENSION_TYPE]: () => <></>,
		[LINKS_MENU_EXTENSION_TYPE]: () => <></>,
		[CARDS_EXTENSION_TYPE]: () => <></>,
		[carouselExtensionType]: () => <></>,
		[SECTION_EXTENSION_TYPE]: () => <></>,
	};

	const defaultExtensionHandlerProps = useMemo(
		() => ({
			contentId,
			propsProvider: PropsProvider,
			mode: ExtensionMode.RENDERER,
		}),
		[contentId, PropsProvider],
	);

	const spotlightExtension = useCallback(
		(extension: ExtensionParams<SpotlightParameters>) => (
			<SpotlightExtensionLoader {...defaultExtensionHandlerProps} extensionNode={extension} />
		),
		[defaultExtensionHandlerProps],
	);

	const linksMenuExtension = useCallback(
		(extension: ExtensionParams<LinksMenuParameters>) => (
			<LinksMenuLoader {...defaultExtensionHandlerProps} extensionNode={extension} />
		),
		[defaultExtensionHandlerProps],
	);

	const cardsExtension = useCallback(
		(extension: ExtensionParams<CardsExtensionParameters>) => (
			<CardsExtensionLoader {...defaultExtensionHandlerProps} extensionNode={extension} />
		),
		[defaultExtensionHandlerProps],
	);

	const carouselExtension = useCallback(
		(extension: ExtensionParams<CarouselParameters>) => (
			<CarouselLoader {...defaultExtensionHandlerProps} extensionNode={extension} />
		),
		[defaultExtensionHandlerProps],
	);

	const sectionExtension = useCallback(
		(extension: ExtensionParams<SectionParameters>) => (
			<SectionExtensionLoader {...defaultExtensionHandlerProps} extensionNode={extension} />
		),
		[defaultExtensionHandlerProps],
	);

	if (contentId) {
		extensionHandlers[SPOTLIGHT_EXTENSION_TYPE] = spotlightExtension;
		extensionHandlers[LINKS_MENU_EXTENSION_TYPE] = linksMenuExtension;
		extensionHandlers[CARDS_EXTENSION_TYPE] = cardsExtension;
		extensionHandlers[carouselExtensionType] = carouselExtension;
		extensionHandlers[SECTION_EXTENSION_TYPE] = sectionExtension;
	}

	return extensionHandlers;
};
